import { Helmet } from 'react-helmet-async';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { UserForm } from '../../components/admin/UserForm';
import { Search, Plus, Edit2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { getUsers, updateUser, deleteUser, createUser } from '../../services/storage';
import { v4 as uuidv4 } from '../../lib/uuid';
import type { User } from '../../types';

export const Users = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>(getUsers());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleStatus = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      updateUser(userId, { status: newStatus });
      setUsers(getUsers());
    }
  };

  const handleDelete = (userId: string) => {
    if (confirm('Tem certeza que deseja excluir este usuário?')) {
      deleteUser(userId);
      setUsers(getUsers());
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setSuccessMessage('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCreateUser = (userData: Omit<User, 'id' | 'createdAt'>) => {
    const newUser: User = {
      ...userData,
      id: uuidv4(),
      createdAt: new Date().toISOString(),
      status: 'active'
    };

    createUser(newUser);
    setUsers(getUsers());
    setSuccessMessage(`Usuário ${userData.name} criado com sucesso!`);
    handleCloseModal();

    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Usuários | Portal Ethics</title>
      </Helmet>

      <div className="space-y-6">
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {successMessage}
          </div>
        )}

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Usuários</h1>
            <p className="text-neutral-600">Gerencie todos os usuários do sistema</p>
          </div>
          <Button 
            variant="primary" 
            className="flex items-center gap-2"
            onClick={handleOpenModal}
          >
            <Plus className="w-4 h-4" />
            Novo Usuário
          </Button>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <Input
              placeholder="Buscar usuários..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Users Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Usuário</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Email</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Função</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-neutral-700">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-blue rounded-full flex items-center justify-center text-white font-semibold">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-neutral-900">{user.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-neutral-600">{user.email}</td>
                    <td className="px-6 py-4">
                      <Badge variant={user.role === 'admin' ? 'primary' : 'secondary'}>
                        {user.role === 'admin' ? 'Admin' : 'Cliente'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(user.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          user.status === 'active' ? 'bg-green-500' : 'bg-neutral-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            user.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button className="p-2 text-neutral-400 hover:text-primary-blue transition-colors">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(user.id)}
                          className="p-2 text-neutral-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Modal para Novo Usuário */}
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title="Novo Usuário"
        >
          <UserForm
            onSubmit={handleCreateUser}
            onCancel={handleCloseModal}
          />
        </Modal>
      </div>
    </DashboardLayout>
  );
};